import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { CampaignInstanceStatusId } from '@backModels/Ref.CampaignInstanceStatus';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { CampaignService } from 'src/app/campaigns/campaign/campaign.service';
import { CompanyService } from 'src/app/companies/company/company.service';
import { Breadcrumb } from 'src/app/models/Breadcrumb';
import { CurrentUserService } from './current-user.service';
import { TextsService } from './texts.service';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private readonly router = inject(Router);

  translate = inject(TranslateService);
  campaignService = inject(CampaignService);
  companyService = inject(CompanyService);
  currentUserService = inject(CurrentUserService);
  textsService = inject(TextsService);

  breadcrumbs = signal<Breadcrumb[]>([]);
  breadcrumbs$ = computed(() => this.breadcrumbs);
  currentParams = signal<Record<string, string>>({});

  root!: ActivatedRouteSnapshot;

  constructor() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.root = this.router.routerState.snapshot.root;
      this.currentParams.set(this.getRouteParams(this.root)); // Actualiza los params
      if (this.textsService.translationsLoadedSignal()) {
        this.updateBreadcrumbs(this.root);
      }
    });

    effect(
      () => {
        this.campaignService.campaign(); // Escucha cambios en campaignSignal
        this.campaignService.campaignInstanceStatusId(); // Escucha cambios en campaignInstanceStatusId
        this.companyService.company();

        if (this.textsService.translationsLoadedSignal()) {
          this.updateBreadcrumbs(this.root);
        }
      },
      { allowSignalWrites: true },
    );
  }

  private updateBreadcrumbs(route: ActivatedRouteSnapshot) {
    if (route != null) {
      const breadcrumbs = this.createBreadcrumbs(route);
      this.breadcrumbs.set(breadcrumbs); // Actualiza el signal
    }
  }

  private createBreadcrumbs(route: ActivatedRouteSnapshot, url = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
    if (breadcrumbs.length === 0) {
      breadcrumbs.push({ label: this.translate.instant('Home'), url: '/' });
    }

    const children: ActivatedRouteSnapshot[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.url.map(segment => segment.path).join('/');
      let nextUrl = url;
      if (routeURL !== '') {
        nextUrl += `/${routeURL}`;
      }

      let label = child.data['breadcrumb'] as string;
      if (label && !breadcrumbs.some(b => b.url === nextUrl)) {
        if (label.includes('{dynamic')) {
          const match = label.match(/\[([^\]]+)\]/);
          const index = match != null ? parseInt(match[1], 10) : 0;
          label = this.getDynamicLabel(index);
          breadcrumbs.push({ label: label, url: nextUrl });
        } else if (nextUrl != '') {
          breadcrumbs.push({ label: this.translate.instant(label), url: nextUrl });
        }
      }

      this.createBreadcrumbs(child, nextUrl, breadcrumbs);
    }

    return breadcrumbs;
  }

  private getDynamicLabel(index: number): string {
    switch (Object.keys(this.currentParams())[index]) {
      case 'companyGuid':
        return this.companyService.company()?.companyInvariantName ?? 'Loading';
      case 'campaignGuid':
        return this.campaignService.campaign()?.campaignName ?? 'Loading';
      case 'campaignInstanceStatusId': {
        const statusId = this.campaignService.campaignInstanceStatusId()?.toString() ?? 'Loading';
        return this.translate.instant(CampaignInstanceStatusId[parseInt(statusId, 10)] || 'UnknownStatus');
      }
      default:
        return 'Not Found';
    }
  }

  private getRouteParams(route: ActivatedRouteSnapshot, params: any = {}): any {
    Object.assign(params, route.params);
    for (const child of route.children) {
      this.getRouteParams(child, params);
    }
    return params;
  }
}
